import * as React from 'react'
import { useCouponCheck } from './useCouponCheck'
import { refusalMessage } from './checkoutErrors'

// The coupon field: a code becomes part of the order only once Chargebee has
// accepted it (useCouponCheck), and its accepted quote is handed to the page's
// quote so adopting it costs no second estimate.
export function useCouponField({ selectedPlan, details, setDetails, quote }) {
    const [draft, setDraft] = React.useState('')
    const couponCheck = useCouponCheck()

    // A refused check leaves the order as it was; an applied coupon can still
    // stop fitting when the plan or country changes, which the quote reports.
    const error = couponCheck.error
        ? refusalMessage(
              couponCheck.error,
              'Could not check this coupon. Please try again.'
          )
        : quote.error?.response?.data?.code === 'COUPON_INVALID'
        ? refusalMessage(quote.error, 'That coupon is not valid.')
        : null
    const saving =
        quote.quote && details.coupon
            ? {
                  amount: (quote.quote.discounts || []).reduce(
                      (total, discount) => total + (discount.amount || 0),
                      0
                  ),
                  currency: quote.quote.currency,
              }
            : null
    const applied =
        Boolean(details.coupon) && details.coupon === draft.trim() && !error

    async function apply() {
        const code = draft.trim()
        if (!code || !selectedPlan || couponCheck.checking) return
        const inputs = {
            planId: selectedPlan.planId,
            country: details.country,
            vatNumber: details.vatNumber,
            coupon: code,
        }
        const acceptedQuote = await couponCheck.check(inputs)
        if (!acceptedQuote) return
        quote.prime(inputs, acceptedQuote)
        setDetails(current => ({ ...current, coupon: code }))
    }

    // Emptying the field takes the applied coupon off.
    function change(value) {
        setDraft(value)
        couponCheck.reset()
        if (!value.trim()) setDetails(current => ({ ...current, coupon: '' }))
    }

    return {
        draft,
        change,
        apply,
        checking: couponCheck.checking,
        error,
        saving,
        applied,
        appliedCode: details.coupon,
    }
}
